import { Circle, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { CustomTooltip } from '../../../../theme/CustomComponents';
import { Project, ProjectScopeAreaType, ScopeFinding } from '../../../../Types/dealGPT';
import { discoveryScopesPathUrl } from '../../../../util/routesUrls';

const ScopeListItem = styled(ListItem)(() => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'left',
  width: '100%',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  borderBottom: '1px solid #e0e0e0', // divider
}));

interface ScopeSelectorProps {
  project: Project;
  sowScopes: ScopeFinding[];
  selectedScopes: ScopeFinding[];
  setSelectedScopes: React.Dispatch<React.SetStateAction<ScopeFinding[]>>;
}

const ScopeSelector: React.FC<ScopeSelectorProps> = ({
  project,
  sowScopes,
  selectedScopes,
  setSelectedScopes,
}) => {
  const { data: scopes } = useFetchData<ProjectScopeAreaType[]>({
    urlPath: discoveryScopesPathUrl(project.projectType),
  });

  const onScopeSelectionChange = useCallback(
    (scope: string) => {
      setSelectedScopes((prev) => {
        const isSelected = prev.find((s) => s.scope === scope);
        if (isSelected) {
          return prev.filter((s) => s.scope !== scope);
        }
        const sowScope = sowScopes.find((s) => s.scope === scope);
        return [...prev, sowScope ?? { scope, findings: '' }];
      });
    },
    [sowScopes, setSelectedScopes]
  );

  useEffect(() => {
    if (sowScopes.length > 0) {
      setSelectedScopes(sowScopes);
    } else {
      setSelectedScopes(project.scopes || []);
    }
  }, [scopes, sowScopes, project.scopes, setSelectedScopes]);

  return (
    <Box sx={{ maxWidth: 360 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Scopes
      </Typography>
      <List>
        {scopes?.map(({ scope }) => {
          const selected = selectedScopes.find((s) => s.scope === scope);
          const findings = selected?.findings ?? sowScopes.find((s) => s.scope === scope)?.findings;
          return (
            <ScopeListItem key={scope} onClick={() => onScopeSelectionChange(scope)}>
              <Checkbox
                edge="start"
                checked={!!selected}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onScopeSelectionChange(scope)}
                sx={{
                  '&.Mui-checked': {
                    color: '#1DD566',
                  },
                }}
              />
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Circle sx={{ color: selected ? '#1DD566' : '#F52C00' }} />
              </ListItemIcon>
              <ListItemText primary={<Typography>{scope}</Typography>} />
              {findings && (
                <CustomTooltip title={findings} arrow>
                  <InfoOutlined />
                </CustomTooltip>
              )}
            </ScopeListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default ScopeSelector;
